import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import D3HelioChart from "./D3HelioChart";
import ChartFilters from "./ChartFilters";
import "./HelioChart.css";

const SIGNS = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
];

const PLANET_SYMBOLS = {
  mercury: '☿',
  venus: '♀',
  earth: '♁',
  mars: '♂',
  jupiter: '♃',
  saturn: '♄',
  uranus: '♅',
  neptune: '♆',
  pluto: '♇',
};

const ZODIAC_SYMBOLS = {
  Aries: '♈',
  Taurus: '♉',
  Gemini: '♊',
  Cancer: '♋',
  Leo: '♌',
  Virgo: '♍',
  Libra: '♎',
  Scorpio: '♏',
  Sagittarius: '♐',
  Capricorn: '♑',
  Aquarius: '♒',
  Pisces: '♓',
};

const SAMPLE_PLANETS = [
  // Note: Sun is not included as it's the center point in heliocentric charts
  { name: "mercury", longitude: 18.14 },
  { name: "venus", longitude: 15.7 },
  { name: "earth", longitude: 255.95 },
  { name: "mars", longitude: 149.7 },
  { name: "jupiter", longitude: 108.5 },
  { name: "saturn", longitude: 153.7 },
  { name: "uranus", longitude: 324.63 },
  { name: "neptune", longitude: 322.92 },
  { name: "pluto", longitude: 269.55 },
];

const SAMPLE_ASPECTS = [
  {
    planets: ["mercury", "mars"],
    aspect: "square",
    type: "major",
    orb: 0.98,
    applying: true,
    strength: { id: "very_strong", label: "Very Strong", icon: "⭐" },
  },
  {
    planets: ["mercury", "neptune"],
    aspect: "trine",
    type: "major",
    orb: 2.1,
    applying: false,
    strength: { id: "strong", label: "Strong" },
  },
  {
    planets: ["venus", "jupiter"],
    aspect: "sextile",
    type: "major",
    orb: 4.2,
    applying: true,
    strength: { id: "moderate", label: "Moderate" },
  },
];

const DEFAULT_ORB_LEGEND = [
  { id: "very_strong", label: "Very Strong (Exact)", range: [0, 1], icon: "⭐" },
  { id: "strong", label: "Strong (Tight)", range: [1, 3] },
  { id: "moderate", label: "Moderate", range: [3, 6] },
];

const ASPECT_COLOR_LEGEND = [
  {
    id: "conjunction",
    label: "Conjunction",
    description: "Fusion / merging of planetary energies",
    color: "linear-gradient(135deg, rgba(99,102,241,0.95), rgba(79,70,229,0.85))",
  },
  {
    id: "opposition",
    label: "Opposition",
    description: "Polarity tension seeking balance",
    color: "linear-gradient(135deg, rgba(239,68,68,0.95), rgba(185,28,28,0.85))",
  },
  {
    id: "square",
    label: "Square",
    description: "Catalyst / challenge for growth",
    color: "linear-gradient(135deg, rgba(245,158,11,0.95), rgba(217,119,6,0.85))",
  },
  {
    id: "trine",
    label: "Trine",
    description: "Flowing harmony / natural talent",
    color: "linear-gradient(135deg, rgba(16,185,129,0.95), rgba(5,150,105,0.85))",
  },
  {
    id: "sextile",
    label: "Sextile",
    description: "Opportunity / creative dialogue",
    color: "linear-gradient(135deg, rgba(59,130,246,0.95), rgba(37,99,235,0.85))",
  },
  {
    id: "minor",
    label: "Minor Aspects",
    description: "Subtle catalysts (dashed line)",
    color: "linear-gradient(135deg, rgba(100,116,139,0.8), rgba(71,85,105,0.75))",
  },
];

const formatLongitude = (longitude) => {
  const normalized = ((Number(longitude) % 360) + 360) % 360;
  const signIndex = Math.floor(normalized / 30);
  const signDegree = normalized % 30;
  const degree = Math.floor(signDegree);
  const minutes = Math.round((signDegree - degree) * 60);
  return {
    sign: SIGNS[signIndex] || '',
    degree,
    minutes,
    decimal: signDegree,
  };
};

const truncateTwoDecimals = (value) => {
  const factor = 100;
  const truncated = value >= 0
    ? Math.floor(value * factor)
    : Math.ceil(value * factor);
  return (truncated / factor).toFixed(2);
};

const formatDegreeString = (planetOrLongitude) => {
  const source = planetOrLongitude && typeof planetOrLongitude === 'object'
    ? planetOrLongitude.longitude
    : planetOrLongitude;
  const lon = Number(source) || 0;
  const { sign, degree, minutes, decimal } = formatLongitude(lon);

  // Calculate degreeInSign (degree within the sign)
  const decimalDeg = degree + minutes / 60;
  const degreeInSignFormatted = truncateTwoDecimals(decimalDeg);

  // Format full longitude with 2 decimal places (no rounding, truncate)
  const longitudeFormatted = truncateTwoDecimals(lon);

  // Get zodiac symbol for the sign
  const signSymbol = getSignSymbol(sign);
  const signWithSymbol = signSymbol ? `${sign} ${signSymbol}` : sign;

  // Return both: degreeInSign and full longitude
  return `${degreeInSignFormatted}° ${signWithSymbol} (${longitudeFormatted}°)`;
};

const upperFirst = (value) => {
  if (!value || typeof value !== 'string') return value;
  // Convert snake_case to Title Case with hyphens
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
};

const getPlanetSymbol = (planet) => {
  return PLANET_SYMBOLS[planet.name?.toLowerCase()] || '';
};

const getSignSymbol = (sign) => {
  return ZODIAC_SYMBOLS[sign] || '';
};

// Regular planets (excluding dwarf planets and Sun which is the center)
const REGULAR_PLANETS = [
  'mercury',
  'venus',
  'earth',
  'mars',
  'jupiter',
  'saturn',
  'uranus',
  'neptune',
  'pluto',
];

const HIDDEN_PLANETS_IN_TABLE = new Set(['sun']);

const dedupeAspects = (aspects = []) => {
  const seen = new Map();
  const result = [];
  const normalizeName = (value) =>
    typeof value === 'string' ? value.toLowerCase() : '';

  aspects.forEach((aspect) => {
    if (!Array.isArray(aspect?.planets) || aspect.planets.length < 2) {
      result.push(aspect);
      return;
    }

    const [first = '', second = ''] = aspect.planets;
    const nameA = normalizeName(first);
    const nameB = normalizeName(second);
    const pairKey = [nameA, nameB].sort().join('__');
    const aspectKey = `${pairKey}:${aspect.aspect?.toLowerCase() || ''}`;

    if (!nameA && !nameB) {
      result.push(aspect);
      return;
    }

    if (!seen.has(aspectKey)) {
      seen.set(aspectKey, result.length);
      result.push(aspect);
      return;
    }

    const existingIndex = seen.get(aspectKey);
    const existing = result[existingIndex];
    const existingOrb = Math.abs(existing?.orb ?? Number.POSITIVE_INFINITY);
    const newOrb = Math.abs(aspect?.orb ?? Number.POSITIVE_INFINITY);

    if (newOrb < existingOrb) {
      result[existingIndex] = aspect;
    }
  });

  return result;
};

const formatLegendRange = (range) => {
  if (!Array.isArray(range) || range.length !== 2) {
    return null;
  }

  return `${range[0]}° – ${range[1]}°`;
};

const formatOrb = (orb) => {
  if (!Number.isFinite(orb)) {
    return '-';
  }

  if (orb === 0) {
    return '0.00';
  }

  const absolute = Math.abs(orb);
  const adjusted = absolute < 0.01 ? 0.01 : absolute;
  return adjusted.toFixed(2);
};

const HelioChart = ({
  planets,
  aspects,
  orbLegend,
}) => {
  const chartPlanets = planets?.length ? planets : SAMPLE_PLANETS;
  const chartAspects = aspects?.length ? aspects : SAMPLE_ASPECTS;
  const orbStrengthLegend = orbLegend?.length ? orbLegend : DEFAULT_ORB_LEGEND;

  // State for filtered data
  const [filteredPlanets, setFilteredPlanets] = useState(chartPlanets);
  const [filteredAspects, setFilteredAspects] = useState(chartAspects);
  const [displayMode, setDisplayMode] = useState('all');

  const isRegularPlanet = (planetName) =>
    REGULAR_PLANETS.includes(planetName?.toLowerCase());

  // Separate regular planets from dwarf planets
  const regularPlanets = filteredPlanets.filter(
    (planet) =>
      isRegularPlanet(planet.name) &&
      !HIDDEN_PLANETS_IN_TABLE.has(planet.name?.toLowerCase())
  );
  const dwarfPlanets = filteredPlanets.filter(
    (planet) =>
      !isRegularPlanet(planet.name) &&
      !HIDDEN_PLANETS_IN_TABLE.has(planet.name?.toLowerCase())
  );
  const dedupedAspects = useMemo(
    () => dedupeAspects(filteredAspects),
    [filteredAspects],
  );

  const strengthColumns = orbStrengthLegend.map((item) => ({
    ...item,
    rangeLabel: formatLegendRange(item.range),
  }));

  const groupedAspects = strengthColumns.map((column) => ({
    ...column,
    aspects: dedupedAspects.filter(
      (aspect) => (aspect.strength?.id || 'moderate') === column.id,
    ),
  }));

  return (
    <div className="helio-chart">
      <ChartFilters
        planets={chartPlanets}
        aspects={chartAspects}
        onPlanetsChange={setFilteredPlanets}
        onAspectsChange={setFilteredAspects}
        onDisplayModeChange={setDisplayMode}
        displayMode={displayMode}
      />

      <div className="helio-chart__visual">
        <div className="helio-chart__visual-charts">
          <D3HelioChart
            planets={filteredPlanets}
            aspects={dedupedAspects}
            width={800}
            height={800}
          />
        </div>
        <div className="helio-chart__legend">
          <div className="legend-section">
            <h4>Orb Strength Guide</h4>
            <ul>
              {orbStrengthLegend.map((item) => (
                <li key={item.id}>
                  <span className={`legend-swatch legend-swatch--${item.id}`}>
                    {item.icon ? (
                      <span className="legend-icon">{item.icon}</span>
                    ) : null}
                  </span>
                  <div className="legend-text">
                    <span className="legend-label">{item.label}</span>
                    {item.range?.length === 2 && (
                      <span className="legend-range">
                        {formatLegendRange(item.range)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="legend-section">
            <h4>Aspect Color Key</h4>
            <ul>
              {ASPECT_COLOR_LEGEND.map((item) => (
                <li key={item.id}>
                  <span
                    className="legend-swatch legend-swatch--color"
                    style={{ background: item.color }}
                  />
                  <div className="legend-text">
                    <span className="legend-label">{item.label}</span>
                    <span className="legend-range">{item.description}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="helio-chart__details">
        <section className="helio-chart__panel helio-chart__panel--planets">
          <header>
            <h4>Planetary Positions</h4>
            <p>Powered by Swiss Ephemeris and NASA JPL Horizons</p>
          </header>
          <table>
            <colgroup>
              <col className="col-planet" />
              <col className="col-longitude" />
            </colgroup>
            <thead>
              <tr>
                <th>Planet</th>
                <th>Longitude</th>
              </tr>
            </thead>
            <tbody>
              {regularPlanets.map((planet, index) => {
                const planetSymbol = getPlanetSymbol(planet);
                const planetName = upperFirst(planet.label || planet.name);
                const displayName = planetSymbol ? `${planetName} ${planetSymbol}` : planetName;
                return (
                  <tr key={`${planet.name}-${index}`}>
                    <td>{displayName}</td>
                    <td>{formatDegreeString(planet)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        {dwarfPlanets.length > 0 && (
          <section className="helio-chart__panel helio-chart__panel--dwarf-planets">
            <header>
              <h4>Dwarf Planets & Asteroids</h4>
              <p>
                Higher octave bodies revealing soul-level patterns and evolutionary missions
              </p>
            </header>
            <table>
              <colgroup>
                <col className="col-planet" />
                <col className="col-longitude" />
              </colgroup>
              <thead>
                <tr>
                  <th>Body</th>
                  <th>Longitude</th>
                </tr>
              </thead>
              <tbody>
                {dwarfPlanets.map((planet, index) => {
                  const planetSymbol = getPlanetSymbol(planet);
                  const planetName = upperFirst(planet.label || planet.name);
                  const displayName = planetSymbol ? `${planetName} ${planetSymbol}` : planetName;
                  return (
                    <tr key={`${planet.name}-${index}`}>
                      <td>{displayName}</td>
                      <td>{formatDegreeString(planet)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        <section className="helio-chart__panel helio-chart__panel--aspects">
          <header>
            <h4>Aspects & Orbs</h4>
            <p>Orb-tight aspects indicate stronger evolutionary resonance.</p>
          </header>
          <div className="aspects-grid">
            {groupedAspects.map((group) => (
              <div className="aspect-column" key={group.id}>
                <div className="aspect-column__header">
                  <span className={`legend-swatch legend-swatch--${group.id}`}>
                    {group.icon || null}
                  </span>
                  <div>
                    <div className="legend-label">{group.label}</div>
                    {group.rangeLabel ? (
                      <div className="legend-range">{group.rangeLabel}</div>
                    ) : null}
                  </div>
                </div>
                {group.aspects.length === 0 ? (
                  <p className="aspect-column__empty">No aspects in this range.</p>
                ) : (
                  <ul className="aspect-column__list">
                    {group.aspects.map((aspect, index) => (
                      <li key={`${aspect.planets.join('-')}-${aspect.aspect}-${index}`}>
                        <div className="aspect-item__title">
                          {`${upperFirst(aspect.planets[0])} ${upperFirst(
                            aspect.aspect,
                          )} ${upperFirst(aspect.planets[1])}`}
                        </div>
                        <div className="aspect-item__meta">
                          <span>{`${formatOrb(aspect.orb)}° orb`}</span>
                          <span>{aspect.applying ? 'Applying' : 'Separating'}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
      <div className="aspects-explanation w-full flex justify-center items-center flex-col">
        <p><span className="font-bold">Applying:</span> It means strengthening and approaching its peak. It also points to an aspect approaching exactness. (Future-focused)</p>
        <p><span className="font-bold">Separating:</span> It means this aspect has peaked and is now fading. This also points to the fact that the aspect has already been exact. (Past-focused)</p>
      </div>
    </div>
  );
};

HelioChart.propTypes = {
  planets: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      longitude: PropTypes.number.isRequired,
      degreeInSign: PropTypes.number,
      sign: PropTypes.string,
      label: PropTypes.string,
      retrograde: PropTypes.bool,
    })
  ),
  aspects: PropTypes.arrayOf(
    PropTypes.shape({
      planets: PropTypes.arrayOf(PropTypes.string).isRequired,
      aspect: PropTypes.string.isRequired,
      type: PropTypes.string,
      orb: PropTypes.number.isRequired,
      applying: PropTypes.bool,
      strength: PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        icon: PropTypes.string,
      }),
    })
  ),
  orbLegend: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      range: PropTypes.arrayOf(PropTypes.number),
    })
  ),
};

HelioChart.defaultProps = {
  planets: undefined,
  aspects: undefined,
  orbLegend: undefined,
};

export default HelioChart;
